/**
 * Traduce los errores de Supabase Auth a mensajes en español para el
 * usuario. Se usa el `code` cuando existe (estable) y, si no, el texto.
 */
export interface AuthErrorLike {
  message: string;
  status?: number;
  code?: string;
  name?: string;
}

/** Errores que piden mostrar el botón "Reenviar correo". */
export const isEmailNotConfirmed = (err: AuthErrorLike | null): boolean =>
  !!err && (err.code === 'email_not_confirmed' || /email not confirmed/i.test(err.message));

/**
 * Fallo de infraestructura (sin red, Supabase caído): solo entonces se
 * pasa al modo local. Un 4xx (contraseña corta, correo sin confirmar,
 * credenciales malas…) es una respuesta válida y se muestra al usuario.
 */
export const isInfraError = (err: AuthErrorLike | null): boolean => {
  if (!err) return false;
  if (typeof err.status === 'number' && err.status >= 400 && err.status < 500) return false;
  if (typeof err.status === 'number' && err.status >= 500) return true;
  // supabase-js marca los fallos de red como AuthRetryableFetchError (status 0 o sin status).
  if (err.name === 'AuthRetryableFetchError') return true;
  return /failed to fetch|networkerror|network request failed|load failed|fetch failed/i.test(err.message);
};

const BY_CODE: Record<string, string> = {
  email_not_confirmed: 'Tu correo aún no está confirmado. Revisa tu bandeja de entrada (y la carpeta de spam) o pulsa «Reenviar correo».',
  invalid_credentials: 'Email o contraseña incorrectos.',
  user_already_exists: 'Ese correo ya está registrado. Inicia sesión o recupera tu contraseña.',
  email_exists: 'Ese correo ya está registrado. Inicia sesión o recupera tu contraseña.',
  weak_password: 'La contraseña es demasiado débil: usa al menos 6 caracteres.',
  over_email_send_rate_limit: 'Se enviaron demasiados correos. Espera unos minutos y vuelve a intentarlo.',
  over_request_rate_limit: 'Demasiados intentos seguidos. Espera un momento y vuelve a intentarlo.',
  email_address_invalid: 'El email no es válido.',
  validation_failed: 'Revisa los datos: el email o la contraseña no son válidos.',
  signup_disabled: 'El registro de cuentas nuevas está desactivado.',
  same_password: 'La nueva contraseña debe ser distinta de la anterior.',
  session_not_found: 'El enlace caducó o ya se usó. Pide uno nuevo.',
  session_expired: 'Tu sesión caducó. Vuelve a iniciar sesión.',
  otp_expired: 'El enlace del correo caducó o ya se usó. Pide uno nuevo.',
  user_not_found: 'No hay ninguna cuenta con ese email.',
};

const BY_TEXT: Array<[RegExp, string]> = [
  [/email not confirmed/i, BY_CODE.email_not_confirmed!],
  [/invalid login credentials/i, BY_CODE.invalid_credentials!],
  [/already (been )?registered|already exists/i, BY_CODE.user_already_exists!],
  [/password should be at least|weak password/i, 'La contraseña debe tener al menos 6 caracteres.'],
  [/rate limit|too many/i, BY_CODE.over_email_send_rate_limit!],
  [/invalid format|unable to validate email|invalid email/i, BY_CODE.email_address_invalid!],
  [/signups not allowed/i, BY_CODE.signup_disabled!],
  [/should be different from the old password/i, BY_CODE.same_password!],
  [/auth session missing|session.*(missing|not found)/i, BY_CODE.session_not_found!],
  [/expired|invalid.*(link|token)/i, BY_CODE.otp_expired!],
];

export const translateAuthError = (err: AuthErrorLike | null): string | null => {
  if (!err) return null;
  if (isInfraError(err)) return 'No se pudo conectar con el servidor. Revisa tu conexión e inténtalo de nuevo.';
  if (err.code && BY_CODE[err.code]) return BY_CODE[err.code]!;
  if (err.status === 429) return BY_CODE.over_request_rate_limit!;
  for (const [re, text] of BY_TEXT) if (re.test(err.message)) return text;
  return `No se pudo completar la operación (${err.message}).`;
};
